import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  Tooltip,
  CollapsibleCard,
  toBool,
  withStyles,
  useForm,
  allCardBrands,
  FieldData,
  ElementContainer,
  BodyText,
  TemplateData,
  sanitizeToNumeric,
} from 'vopay';
import { ElinxInfo, Contact, ElinxType, ElinxInfoSerialized } from './types';
import ElinxSettingsDialogue from '../ElinxSettings/ElinxSettingsDialogue';
import ElinxPreview from '../ElinxPreview/ElinxPreview';
import useFetchElinxSettings from '@/hooks/useApi/useFetchElinxSettings';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import usePreFundingEnabled from '@/hooks/usePreFundingEnabled';
import { useClientData } from '@/providers/ClientData.provider';
import useFetchContactPaymentMethod from '@/hooks/useApi/useFetchContactPaymentMethod';
import {
  useElinxBasicDetailsRows,
  useElinxContactAddressRows,
  useElinxDetailsRows,
  useElinxPaymentSettingsRows,
  useBankAccountRows,
  useCardSettingsRows,
  useElinxRowsContactDetails,
  useElinxInstallmentPlansRows,
  useElinxPartialPaymentRows,
  useElinxSettingsRows,
  mapToPaymentSelectionValue,
  getDefaultPaymentSelectionMethods,
  getDefaultBankingPaymentRails,
  isOneTimeScheduledForToday,
  is3DSApplicable,
  isValidRecipientEmail,
} from './buildElinxRows.helper';
import styles from './ElinxPages.module.scss';
import usePortalDropdownContacts from '@/hooks/useApi/usePortalDropdownContacts';
import useContacts from '@/hooks/useApi/useContacts';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { Button, InfoBanner, UtilButton } from 'UI';
import { useTranslation } from 'react-i18next';
import { useScheduleExpiryMessage } from '@/hooks/useScheduleExpiryMessage';
import usePaymentMethodCurrencySupport from '@/hooks/useFields/TransactionCreationFields/usePaymentMethodCurrencySupport';
import useFetchFundingOptions from '@/hooks/useApi/useFetchFundingOptions';
import usePreFundingSettings from '@/hooks/useApi/usePreFundingSettings';
import useFetchDefaultOperationalBankAccount from '@/hooks/useApi/useFetchDefaultOperationalBankAccount';
import { getAvailableBalance } from '@/hooks/useFields/TransactionCreationFields/useFundingOptionField';
import { useToast } from '@/providers/Toast.provider';
import ElinxFundingBanner from './ElinxFundingBanner';

export type ElinxFormProps = {
  formType: ElinxType;
  onSubmit: (values: ElinxInfoSerialized) => void;
};
const classes = withStyles(styles);

// The contact's saved extra emails are pre-filled into Additional Recipients Email, whose validation
// rejects the whole form on any invalid entry. Earthnode saves every address an eLinx request went to, and
// its looser email check lets through values this rule rejects (two addresses run together; a bare "1" on
// sandbox), so a saved value can be one the user never typed and the form gives no reason for. No spec
// covers this; an address that can't be delivered to is dropped rather than surfaced, because keeping it
// only locks Review (ID-14010).
function deliverableSavedEmails(emails: string[] | undefined): string[] {
  return (emails || []).filter(isValidRecipientEmail);
}

// Skip the grid cell for fields with no label and no template.
const renderBasicDetailField = (templateData: TemplateData) => {
  const showingLabel = templateData.label || templateData.inputState === 'required' || templateData.tooltip;
  if (!templateData.template && !showingLabel) return null;

  const element = (
    <ElementContainer
      key={templateData.fieldName}
      padding={'0 0 8px'}
      label={
        showingLabel ? (
          <Box ai="center" gapSize={templateData.inputState === 'required' ? '2px' : '4px'}>
            {templateData.label}
            {templateData.inputState === 'required' && <span style={{ color: 'red' }}>*</span>}
            {templateData.tooltip}
          </Box>
        ) : null
      }
      isBold={false}
      input={
        <Box flx="col" gapSize="8px">
          {templateData.template}
          {templateData.helpText && <BodyText size="sm">{templateData.helpText}</BodyText>}
        </Box>
      }
    />
  );

  // Frequency doubles as the Recurring Payment card's render slot — it must never share the
  // ambient 2-column grid with the next field.
  if (templateData.fieldName === 'Frequency') {
    return (
      <div key={templateData.fieldName} style={{ gridColumn: '1 / -1' }}>
        {element}
      </div>
    );
  }

  // Recurrence must always sit to the left of the Payment/Collection Date field, on the same
  // grid row — pin it explicitly with CSS order/column-start rather than relying on array
  // position, and force it to start a fresh row so it never fills PayFrom's empty second column.
  if (templateData.fieldName === 'Recurrence') {
    return (
      <div key={templateData.fieldName} style={{ order: 1, gridColumnStart: 1 }}>
        {element}
      </div>
    );
  }

  if (templateData.fieldName === 'ScheduleStartDate') {
    return (
      <div key={templateData.fieldName} style={{ order: 2 }}>
        {element}
      </div>
    );
  }

  return element;
};

const ElinxForm: React.FC<ElinxFormProps> = ({ formType, onSubmit }) => {
  const alertDialog = useAlertDialog();

  const {
    response: {
      Country,
      USDEFTCollectEnabled,
      USDEFTSendEnabled,
      CADCreditCardCollectionEnabled,
      USDCreditCardCollectionEnabled,
      AccountName,
      clientAccountsEnabled,
      CreditCardEnabled,
      CreditCardProcessor,
    },
  } = useClientData();

  const isTrigger3DSVisible = toBool(CreditCardEnabled) && CreditCardProcessor === 'globalpay';

  const preFundingEnabled = usePreFundingEnabled();
  // Either rail lets a Canadian account collect USD: EFT, or a card charged through a processor
  // holding USD credentials.
  const isUSDCollectEnabled = toBool(USDEFTCollectEnabled) || USDCreditCardCollectionEnabled;

  const { dropdownContacts, getDropdownItems } = usePortalDropdownContacts({
    includeBalance: formType === 'Pay',
  });

  const [selectedContactBalance, setSelectedContactBalance] = useState<number | undefined>(undefined);

  const { paymentMethods, fetchPaymentMethods } = useFetchContactPaymentMethod();
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form' });
  const { fetchContact } = useContacts();
  const { fetchClientAccount } = useClientAccounts();
  const toast = useToast();

  // Pay From (formType 'Pay' only) — mirrors the Payments page: the account's own funding
  // options/trigger, not the contact's.
  const { fundingOptions, fetchFundingOptions, fetching: fundingOptionsFetching } = useFetchFundingOptions();
  const { fundingTrigger, fetchPreFundingSettings } = usePreFundingSettings();
  const { operationalBankAccount, fetchDefaultOperationalBankAccount } = useFetchDefaultOperationalBankAccount();

  const availableBalance = useMemo(
    () => getAvailableBalance(fundingOptions.find((option) => option.Source === 'balance')),
    [fundingOptions],
  );

  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 1);

  const defaultValues: ElinxInfo = {
    Contact: '',
    Currency: Country === 'US' ? 'USD' : 'CAD',
    Amount: '0',
    Frequency: 'single',
    Recurrence: 'one-time',
    NameOfFrequency: 'monthly',
    ScheduleStartDate: Date.now().toString(),
    ScheduleEndDate: endDate.getTime().toString(),
    Ending: 'End Date',
    TransactionLabel: '',
    Note: '',
    ContactType: 'individual',
    ExistingContactName: '',
    FirstName: '',
    LastName: '',
    BusinessName: '',
    NewEmailAddress: '',
    AddressLine1: '',
    AddressLine2: '',
    City: '',
    Province: '',
    PostalCode: '',
    Country: Country || 'CA',
    PaymentMethodDisplayOptions: undefined,
    PaymentSelectionMethod: '',
    AccountSelectionMethod: '',
    SenderName: AccountName,
    RecipientMessage: '',
    InvoiceNumber: '',
    InvoiceName: '',
    InvoiceFile: '',
    ScheduleRemainingAmount: false,
    FlexiblePaymentOptions: '',
    RemainderPaymentDate: endDate.getTime().toString(),
    ReceiverPhoneNumber: '',
    SendNotification: false,
    SendReminder: false,
    ReminderFrequency: '7',
    NotificationMethod: 'email',
    AcceptedCardBrands: allCardBrands.join(','),
    CardTypeValidation: false,
    Trigger3DS: false,
    AdditionalRecipientEmails: [],
  };

  const [isVisible, setIsVisible] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isUnderReview, setIsUnderReview] = useState(false);

  const [, , , fetchElinxSettings] = useFetchElinxSettings({});
  const [showingMore, setShowingMore] = useState(false);
  const [isNewContact, setIsNewContact] = useState(false);
  const [hasMultiplePaymentMethods, setHasMultiplePaymentMethods] = useState(false);
  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [isInstallmentPlan, setIsInstallmentPlan] = useState(false);
  const [elinxExpirationDays, setElinxExpirationDays] = useState<number>();
  const lockedFeatures = useLockedFeatures();
  const { isSelectionMethodAvailable } = usePaymentMethodCurrencySupport(formType);
  const { updateIsLocked } = useLockedFeatureDialogue();
  const { permissions } = usePermissions();

  async function fetchSelectedContact(id: string): Promise<Contact | null> {
    try {
      if (clientAccountsEnabled) {
        const ca = await fetchClientAccount(id);
        if (!ca) return null;

        const contact: Contact = {
          id: ca.ClientAccountID,
          name: ca.ClientName,
          email: ca.EmailAddress,
          phone: ca.PhoneNumber,
          accountType: ca.IsBusiness ? 'business' : 'individual',
          additionalContactEmails: deliverableSavedEmails(ca.AdditionalContacts.email),
        };

        setSelectedContact(contact);

        return contact;
      } else {
        const c = await fetchContact(id);
        if (!c) return null;

        const contact: Contact = {
          id: c.ContactID,
          name: c.ContactName,
          email: c.EmailAddress,
          phone: c.PhoneNumber,
          accountType: c.IsBusiness ? 'business' : 'individual',
          additionalContactEmails: deliverableSavedEmails(c.AdditionalContacts.email),
        };

        setSelectedContact(contact);

        return contact;
      }
    } catch (err) {
      console.error(err);

      if (clientAccountsEnabled) {
        alertDialog.error(
          t('Dialogs.FetchClientAccountsError.title', { defaultValue: 'Error' }),
          t('Dialogs.FetchClientAccountsError.content', { defaultValue: 'Failed to Fetch Contacts' }),
          { abortLabel: t('Dialogs.FetchClientAccountsError.abortLabel', { defaultValue: 'Dismiss' }) },
        );
      } else {
        alertDialog.error(
          t('Dialogs.FetchContactsError.title', { defaultValue: 'Error' }),
          t('Dialogs.FetchContactsError.content', { defaultValue: 'Failed to Fetch Contacts' }),
          { abortLabel: t('Dialogs.FetchContactsError.abortLabel', { defaultValue: 'Dismiss' }) },
        );
      }

      return null;
    }
  }

  useEffect(() => {
    getDropdownItems('');
  }, []);

  const basicDetailsRows = useElinxBasicDetailsRows(
    dropdownContacts,
    getDropdownItems,
    showingMore,
    formType,
    Country,
    (formType === 'Pay' && toBool(USDEFTSendEnabled)) ||
      (formType === 'Collect' && isUSDCollectEnabled) ||
      (formType === 'Connect' && (toBool(USDEFTSendEnabled) || isUSDCollectEnabled)),
    // A US account only reaches CAD by charging a card with CAD processor credentials.
    formType === 'Collect' && CADCreditCardCollectionEnabled,
    selectedContactBalance,
    fundingOptions,
    fundingOptionsFetching,
    fundingTrigger,
  );

  // Split so the expiry banner can render right after Payment Date/Recurrence, before Notes.
  const scheduleBreakIndex = basicDetailsRows.findIndex((field) => field.fieldName === 'Recurrence');
  const scheduleFieldsRows = basicDetailsRows.slice(0, scheduleBreakIndex + 1);
  const remainingBasicDetailsRows = basicDetailsRows.slice(scheduleBreakIndex + 1);
  const contactAddressRows = useElinxContactAddressRows(Country);
  const detailsRows = useElinxDetailsRows(formType);

  const [contactName, setContactName] = useState('');

  const paymentSettingsRows = useElinxPaymentSettingsRows(formType, Country, contactName, paymentMethods);
  const bankAccountRows = useBankAccountRows(formType, Country);
  const contactDetailsRows = useElinxRowsContactDetails();
  const installmentPlansRows = useElinxInstallmentPlansRows(formType, isInstallmentPlan);
  const partialPaymentRows = useElinxPartialPaymentRows(formType, isPartialPayment);
  const cardSettingsRows = useCardSettingsRows(formType, isTrigger3DSVisible);

  const settingsRows = useElinxSettingsRows(
    isUnderReview,
    defaultValues.NotificationMethod || 'email',
    selectedContact,
    Country,
  );

  const fieldDataList: FieldData<ElinxInfo>[] = [
    ...basicDetailsRows,
    ...contactDetailsRows,
    ...contactAddressRows,
    ...paymentSettingsRows,
    ...bankAccountRows,
    ...cardSettingsRows,
    ...partialPaymentRows,
    ...installmentPlansRows,
    ...detailsRows,
    ...settingsRows,
  ];

  const { renderFormElements, ctx } = useForm<ElinxInfo, ElinxInfoSerialized>({
    config: fieldDataList,
    initialValues: defaultValues,
    onSubmit: (serializedData, ctx) => {
      const accountType = selectedContact?.accountType;

      onSubmit({
        ...serializedData,
        FlexiblePaymentOptions: [
          hasMultiplePaymentMethods ? 'multiple' : '',
          isPartialPayment ? 'partial' : '',
          isInstallmentPlan ? 'installments' : '',
        ]
          .filter((option) => option !== '')
          .join(','),
        ExistingContactName: ctx.values.ExistingContactName,
        ReceiverPhoneNumber: ctx.values.ReceiverPhoneNumber,
        AccountHolderType: accountType,
        InvoiceName: ctx.values.InvoiceName,
        AdditionalRecipientEmails: serializedData.AdditionalRecipientEmails || [],
      });
    },
    isReviewing: isUnderReview,
  });

  const paymentDate =
    ctx.values.Recurrence && ctx.values.Recurrence !== 'one-time' && ctx.values.ScheduleStartDate
      ? new Date(Number(ctx.values.ScheduleStartDate))
      : undefined;

  const scheduleExpiryMessage = useScheduleExpiryMessage(paymentDate, elinxExpirationDays);

  useEffect(() => {
    if (formType !== 'Pay' || !preFundingEnabled) return;

    (async () => {
      try {
        await fetchPreFundingSettings();
      } catch {
        toast.neutral(t('fetchPreFundingSettingsError', { defaultValue: 'Could not load your pre-funding settings' }));
      }
    })();
  }, [formType, preFundingEnabled]);

  useEffect(() => {
    if (formType !== 'Pay' || !preFundingEnabled) return;

    (async () => {
      try {
        await fetchDefaultOperationalBankAccount({ Currency: ctx.values.Currency || 'CAD' });
      } catch {
        toast.neutral(t('fetchBankAccountError', { defaultValue: 'Could not load your operational bank account' }));
      }
    })();
  }, [formType, preFundingEnabled, ctx.values.Currency]);

  useEffect(() => {
    if (formType !== 'Pay') return;

    // Currency changed, so any previously selected PayFrom may no longer be valid. Without
    // pre-funding the field is hidden, so pin it to 'balance' (no funding transaction).
    ctx.setFieldValue('PayFrom', preFundingEnabled && fundingTrigger === 'never' ? '' : 'balance');
    ctx.setFieldTouched('PayFrom', false);

    if (!preFundingEnabled) return;

    (async () => {
      try {
        await fetchFundingOptions({ Currency: ctx.values.Currency || 'CAD' });
      } catch {
        toast.neutral(t('fetchFundingOptionsError', { defaultValue: 'Could not load your funding options' }));
      }
    })();
  }, [formType, preFundingEnabled, ctx.values.Currency, fundingTrigger]);

  // If the account's funding trigger resolves to non-'never' after Scheduled was already picked
  // (canScheduleElinx then hides Recurrence/ScheduleStartDate/Frequency), force schedule state
  // back off rather than leave it stale — Frequency/ScheduleStartDate still serialize real values
  // once set, regardless of whether their fields are currently visible. A manually-picked Pay
  // From no longer triggers this: Pay From hides itself once Recurrence leaves 'one-time'
  // (mirrors SinglePaymentForm), so scheduling no longer needs to react to it in the other
  // direction.
  useEffect(() => {
    if (
      formType !== 'Pay' ||
      !preFundingEnabled ||
      fundingTrigger === 'never' ||
      !ctx.values.Recurrence ||
      ctx.values.Recurrence === 'one-time'
    ) {
      return;
    }

    ctx.setFieldValue('ScheduleStartDate', '');
    ctx.setFieldValue('Recurrence', 'one-time');
    ctx.setFieldValue('Frequency', 'single');
  }, [formType, preFundingEnabled, fundingTrigger, ctx.values.Recurrence]);

  // Pay From is required whenever it's visible (trigger 'never', unscheduled), so leaving it
  // empty blocks Review with no visible error. Mirrors SinglePaymentForm's auto-suggestion:
  // balance while it covers the amount, bank once it falls short — until the user picks for
  // themselves, after which only a flip between those two outcomes overrides their choice.
  const prevPayFromOutcomeRef = useRef<'covered' | 'short' | undefined>(undefined);

  useEffect(() => {
    if (formType !== 'Pay' || !preFundingEnabled || fundingTrigger !== 'never') return;

    // Scheduled requests hide the field and never create a funding transaction.
    if (ctx.values.Recurrence && ctx.values.Recurrence !== 'one-time') {
      prevPayFromOutcomeRef.current = undefined;

      return;
    }

    // Suggesting before the options land would mean guessing 'short' off a zero balance — on this
    // form that would pre-select bank, i.e. a real funding transaction the user never asked for.
    if (fundingOptionsFetching || fundingOptions.length === 0) return;

    // No real choice to make (no bank/credit for this currency) — the field is hidden, so force
    // 'balance' rather than ever suggesting a source that isn't actually an available option.
    if (fundingOptions.length <= 1) {
      prevPayFromOutcomeRef.current = undefined;
      if (ctx.values.PayFrom !== 'balance') ctx.setFieldValue('PayFrom', 'balance');

      return;
    }

    if (!ctx.values.Amount) {
      prevPayFromOutcomeRef.current = undefined;
      if (!ctx.touched.PayFrom) ctx.setFieldValue('PayFrom', '');

      return;
    }

    const outcome: 'covered' | 'short' =
      Number(sanitizeToNumeric(ctx.values.Amount)) <= availableBalance ? 'covered' : 'short';

    const outcomeChanged = prevPayFromOutcomeRef.current !== undefined && prevPayFromOutcomeRef.current !== outcome;
    prevPayFromOutcomeRef.current = outcome;

    if (!ctx.touched.PayFrom || outcomeChanged) {
      ctx.setFieldValue('PayFrom', outcome === 'covered' ? 'balance' : 'bank');
      ctx.setFieldTouched('PayFrom', false);
    }
  }, [
    formType,
    preFundingEnabled,
    fundingTrigger,
    ctx.values.Amount,
    ctx.values.Currency,
    ctx.values.Recurrence,
    availableBalance,
    fundingOptions,
    fundingOptionsFetching,
  ]);

  useEffect(() => {
    const isNew = ctx.values.Contact === 'new';
    setIsNewContact(isNew);

    if (isNew) {
      const name =
        ctx.values.ContactType === 'business'
          ? ctx.values.BusinessName
          : [ctx.values.FirstName, ctx.values.LastName].filter(Boolean).join(' ');

      setContactName(name || '');
    } else {
      setContactName(ctx.values.ExistingContactName || '');
    }
  }, [
    ctx.values.Contact,
    ctx.values.ContactType,
    ctx.values.ExistingContactName,
    ctx.values.FirstName,
    ctx.values.LastName,
    ctx.values.BusinessName,
  ]);

  useEffect(() => {
    if ((ctx.values.PaymentSelectionMethod || '').split(',').length <= 1) {
      setHasMultiplePaymentMethods(false);
    }
  }, [ctx.values.PaymentSelectionMethod]);

  // Watches the value rather than the field's onChange because the defaults effect below also sets
  // PaymentSelectionMethod (Bank Account included) programmatically.
  const hadBankSelectionRef = useRef(false);

  useEffect(() => {
    const hasBankSelection = (ctx.values.PaymentSelectionMethod || '').split(',').includes('bank');

    if (hasBankSelection && !hadBankSelectionRef.current && !ctx.values.AccountSelectionMethod) {
      ctx.setFieldValue('AccountSelectionMethod', 'manual');
    } else if (!hasBankSelection && hadBankSelectionRef.current) {
      ctx.setFieldValue('AccountSelectionMethod', '');
      ctx.setFieldTouched('AccountSelectionMethod', false);
    }

    hadBankSelectionRef.current = hasBankSelection;
  }, [ctx.values.PaymentSelectionMethod]);

  useEffect(() => {
    if (
      ctx.values.PaymentSelectionMethod &&
      ctx.values.PaymentSelectionMethod !== '' &&
      paymentMethods &&
      ctx.values.PaymentMethodDisplayOptions?.split(',').includes('Existing')
    ) {
      const filteredPaymentMethods = paymentMethods.filter(
        (paymentMethod) =>
          ctx.values.PaymentSelectionMethod?.includes(mapToPaymentSelectionValue(paymentMethod.PaymentMethod)) ||
          !ctx.values.PaymentSelectionMethod ||
          ctx.values.PaymentSelectionMethod === '',
      );

      if (filteredPaymentMethods.length === 0) {
        ctx.setFieldValue('PaymentMethodDisplayOptions', 'New');
      }
    }
  }, [ctx.values.PaymentSelectionMethod, paymentMethods]);

  //TODO: Move this into to the row file with onchange
  useEffect(() => {
    // Only set defaults if fields are completely empty
    if (!ctx.values.PaymentMethodDisplayOptions) {
      ctx.setFieldValue('PaymentSelectionMethod', '');
      ctx.setFieldValue('BankingPaymentRail', '');
      ctx.setFieldTouched('PaymentSelectionMethod', false);
      ctx.setFieldTouched('BankingPaymentRail', false);

      return;
    }

    const defaultPaymentMethods = getDefaultPaymentSelectionMethods(
      formType,
      Country,
      lockedFeatures.LockedSelectOptions,
      ctx.values.ScheduleStartDate,
      ctx.values.Frequency,
      paymentMethods,
      ctx.values.PaymentMethodDisplayOptions,
      (value) => isSelectionMethodAvailable(value, ctx.values.Currency, { isRequested: true }),
    );

    if (defaultPaymentMethods) {
      ctx.setFieldValue('PaymentSelectionMethod', defaultPaymentMethods);
    } else if (
      ctx.values.PaymentSelectionMethod?.split(',')
        .filter(Boolean)
        .some((method) => !isSelectionMethodAvailable(method, ctx.values.Currency, { isRequested: true }))
    ) {
      // There is no default to apply, but what is already selected cannot collect this currency.
      // Left alone it would survive the currency change, satisfy the required check and post a rail
      // the account has no way to collect with. An empty default on its own is not enough to clear
      // on: it is also what a still-loading payment-method list looks like.
      ctx.setFieldValue('PaymentSelectionMethod', '');
    }

    const defaultBankingRails = getDefaultBankingPaymentRails(
      formType,
      Country,
      lockedFeatures.LockedSelectOptions,
      ctx.values.ScheduleStartDate,
      ctx.values.Frequency,
    );

    if (defaultBankingRails) {
      ctx.setFieldValue('BankingPaymentRail', defaultBankingRails);
    }
  }, [
    ctx.values.ScheduleStartDate,
    ctx.values.Frequency,
    paymentMethods,
    ctx.values.PaymentMethodDisplayOptions,
    ctx.values.Currency,
  ]);

  useEffect(() => {
    if (ctx.values.Contact && ctx.values.Contact !== 'new') {
      const balance = dropdownContacts.find((c) => c.id === ctx.values.Contact)?.availableFunds;
      setSelectedContactBalance(balance);

      fetchSelectedContact(ctx.values.Contact).then((contact) => {
        ctx.setFieldValue('ReceiverPhoneNumber', contact?.phone ?? '');
        ctx.setFieldValue('OverrideEmailAddress', contact?.email ?? '');
        ctx.setFieldValue('AdditionalRecipientEmails', contact?.additionalContactEmails ?? []);
      });

      fetchPaymentMethods(ctx.values.Contact);
    } else {
      setSelectedContact(null);
      setSelectedContactBalance(undefined);
      ctx.setFieldValue('ReceiverPhoneNumber', '');
    }
  }, [ctx.values.Contact]);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ctx.setFieldValue(
      'FlexiblePaymentOptions',
      [
        hasMultiplePaymentMethods ? 'multiple' : '',
        isPartialPayment ? 'partial' : '',
        isInstallmentPlan ? 'installments' : '',
      ]
        .filter((option) => option !== '')
        .join(','),
    );
  }, [hasMultiplePaymentMethods, isPartialPayment, isInstallmentPlan]);

  // 3DS only applies to one-time requests; the field is disabled otherwise, so don't post a stale true.
  useEffect(() => {
    if (!is3DSApplicable(formType, ctx.values.Recurrence) && ctx.values.Trigger3DS) {
      ctx.setFieldValue('Trigger3DS', false);
    }
  }, [formType, ctx.values.Recurrence, ctx.values.Trigger3DS]);

  const getElinxSettings = async () => {
    try {
      const res = await fetchElinxSettings({});

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      } else {
        setIsVisible(false);

        ctx.setFieldValue('SendNotification', toBool(res.ELinxSettings.ReceiverNotificationsEnabled));
        ctx.setFieldValue('SendReminder', toBool(res.ELinxSettings.ELinxReminderEnabled));
        ctx.setFieldValue('ReminderFrequency', res.ELinxSettings.ELinxReminderFrequency);
        ctx.setFieldValue('NotificationMethod', res.ELinxSettings.DefaultNotificationMethod);
        ctx.setFieldValue('Trigger3DS', toBool(res.ELinxSettings.Trigger3DS));
        setElinxExpirationDays(Number(res.ELinxSettings.ELinxExpirationTime));
      }
    } catch (err) {
      console.error(err);

      alertDialog.caution(t('Dialogs.FetchElinxSettingsError.title'), t('Dialogs.FetchElinxSettingsError.content'), {
        abortLabel: t('Dialogs.FetchElinxSettingsError.abortLabel'),
      });
    }
  };

  useEffect(() => {
    getElinxSettings();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        width: '100%',
        // when scrolling to the top of this element, add extra scroll beyond the top so we can also see the page title
        scrollMarginTop: '500px',
      }}
    >
      <form onSubmit={ctx.handleSubmit}>
        <Box flx="col" base="8" gap="lg">
          <Card>
            <Box gap="lg" base="8" flx="col">
              <div className={classes('grid')}>
                {renderFormElements({ config: scheduleFieldsRows, override: renderBasicDetailField })}
              </div>

              {scheduleExpiryMessage && <InfoBanner>{scheduleExpiryMessage}</InfoBanner>}

              {formType === 'Pay' && (
                <ElinxFundingBanner
                  fundingTrigger={fundingTrigger}
                  isScheduled={
                    (!!ctx.values.Recurrence && ctx.values.Recurrence !== 'one-time') ||
                    !isOneTimeScheduledForToday(ctx.values)
                  }
                  fundingOptions={fundingOptions}
                  payFrom={ctx.values.PayFrom || ''}
                  amount={ctx.values.Amount || ''}
                  currency={ctx.values.Currency || 'CAD'}
                  operationalBankAccount={operationalBankAccount}
                />
              )}

              <div className={classes('grid')}>
                {renderFormElements({ config: remainingBasicDetailsRows, override: renderBasicDetailField })}
              </div>

              {!showingMore && (
                <Box jc="fs">
                  <UtilButton
                    onClick={() => {
                      setShowingMore(true);
                    }}
                    iconLeft="add"
                  >
                    {t('BasicDetails.moreInformationButton')}
                  </UtilButton>
                </Box>
              )}
            </Box>
          </Card>

          {ctx.values.Contact !== '' && (
            <>
              {isNewContact && (
                <>
                  <Card title={t('ContactDetails.title')}>
                    <div className={classes('grid')}>{renderFormElements({ config: contactDetailsRows })}</div>
                  </Card>

                  <Card title={t('ContactAddress.title')}>
                    <div className={classes('grid')}>{renderFormElements({ config: contactAddressRows })}</div>
                  </Card>
                </>
              )}

              <Card title={t('ContactPaymentSettings.title')}>
                <div className={classes('grid')}>{renderFormElements({ config: paymentSettingsRows })}</div>

                {ctx.values.PaymentSelectionMethod?.includes('bank') && formType !== 'Connect' && (
                  <div className={classes('bankInfoContainer')}>
                    <Card title={t('BankAccount.title')}>
                      <div className={classes('grid')}>{renderFormElements({ config: bankAccountRows })}</div>
                    </Card>
                  </div>
                )}

                {(ctx.values.PaymentSelectionMethod?.includes('credit') ||
                  ctx.values.PaymentSelectionMethod?.includes('debitcard')) && (
                  <div className={classes('bankInfoContainer')}>
                    <Card title={t('CardSettings.title', { defaultValue: 'Card Settings' })}>
                      <div className={classes('grid')}>{renderFormElements({ config: cardSettingsRows })}</div>
                    </Card>
                  </div>
                )}
              </Card>

              {formType == 'Flex' && (
                <Tooltip
                  information={t('MultiplePaymentMethods.disabledTooltip')}
                  hasButton={false}
                  bgColor="transparent"
                  customElement={
                    <Box flx="col" width="100%">
                      <CollapsibleCard
                        onOpenChange={(open) => {
                          setHasMultiplePaymentMethods(open);
                        }}
                        open={hasMultiplePaymentMethods}
                        disabled={!(!isUnderReview && (ctx.values.PaymentSelectionMethod || '').split(',').length > 1)}
                      >
                        <CollapsibleCard.Trigger data-testid="multiplePayments-trigger">
                          <Box gapSize="5px" ai="center">
                            <span>{t('MultiplePaymentMethods.title')}</span>

                            <Tooltip
                              information={t('MultiplePaymentMethods.enabledTooltip')}
                              position="top"
                              width="394px"
                              buttonWidth="20px"
                              isDisabled={!ctx.values.PaymentSelectionMethod}
                            />
                          </Box>
                        </CollapsibleCard.Trigger>

                        <CollapsibleCard.Content className={styles.noBody}>
                          <></>
                        </CollapsibleCard.Content>
                      </CollapsibleCard>
                    </Box>
                  }
                  isDisabled={(ctx.values.PaymentSelectionMethod || '').split(',').length > 1}
                  position="top"
                  width="394px"
                  buttonWidth="20px"
                />
              )}

              {formType == 'Flex' && (
                <CollapsibleCard
                  onOpenChange={(open) => {
                    setIsPartialPayment(open);
                  }}
                  open={isPartialPayment}
                  disabled={isUnderReview}
                >
                  <CollapsibleCard.Trigger data-testid="partialPayment-trigger">
                    <Box gapSize="5px" ai="center">
                      <span>{t('PartialPayment.title')}</span>

                      <Tooltip
                        information={t('PartialPayment.tooltip')}
                        position="top"
                        width="394px"
                        buttonWidth="20px"
                      />
                    </Box>
                  </CollapsibleCard.Trigger>

                  <CollapsibleCard.Content>
                    <div className={classes('grid')}>{renderFormElements({ config: partialPaymentRows })}</div>
                  </CollapsibleCard.Content>
                </CollapsibleCard>
              )}

              {formType == 'Flex' && (
                <CollapsibleCard
                  onOpenChange={(open) => {
                    setIsInstallmentPlan(open);
                  }}
                  open={isInstallmentPlan}
                  disabled={isUnderReview}
                >
                  <CollapsibleCard.Trigger data-testid="installmentPlan-trigger">
                    <Box gapSize="5px" ai="center">
                      <span>{t('InstallmentPlan.title')}</span>

                      <Tooltip
                        information={t('InstallmentPlan.tooltip')}
                        position="top"
                        width="394px"
                        buttonWidth="20px"
                      />
                    </Box>
                  </CollapsibleCard.Trigger>

                  <CollapsibleCard.Content>
                    <div className={classes('grid')}>{renderFormElements({ config: installmentPlansRows })}</div>
                  </CollapsibleCard.Content>
                </CollapsibleCard>
              )}

              <Card title={t('ELinxDetails.title')}>
                <div className={classes('grid', 'spanLastChild')}>{renderFormElements({ config: detailsRows })}</div>
              </Card>

              <Card title={t('ELinxSettings.title')}>
                <div className={classes('grid')}>{renderFormElements({ config: settingsRows })}</div>
              </Card>
            </>
          )}
        </Box>

        <Box jc="sb" mt="lg" base="8">
          <Box jc="fs">
            <Button
              iconRight={!lockedFeatures.PayLinkEnabled || !permissions.ViewElinxSettings ? 'lock' : undefined}
              onClick={() => {
                !lockedFeatures.PayLinkEnabled || !permissions.ViewElinxSettings
                  ? updateIsLocked(true, 'Elinx')
                  : setIsVisible(true);
              }}
            >
              {t('Buttons.editElinxSettings')}
            </Button>
          </Box>

          <Box flx="row" gap="common">
            <Box jc="fe">
              <Button onClick={() => setIsPreviewing(true)}>{t('Buttons.seePreview')}</Button>
            </Box>

            <Box jc="fe" gap="sm">
              {isUnderReview ? (
                <Button
                  onClick={() => {
                    setIsUnderReview(false);
                  }}
                >
                  {t('Buttons.back')}
                </Button>
              ) : null}

              {!isUnderReview && (
                <Button
                  colorScheme="primary"
                  onClick={() => {
                    setIsUnderReview(true);
                    ref.current?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  disabled={!ctx.isValid || Object.values(ctx.errors).length > 0}
                >
                  {t('Buttons.review')}
                </Button>
              )}

              {isUnderReview && (
                <Button
                  type="submit"
                  disabled={!ctx.isValid || Object.values(ctx.errors).length > 0}
                  colorScheme="primary"
                >
                  {t('Buttons.submit')}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </form>

      <ElinxSettingsDialogue
        isVisible={isVisible}
        onCloseModal={() => setIsVisible(false)}
        onFetchElinxSettings={getElinxSettings}
      />

      <ElinxPreview visible={isPreviewing} onClose={() => setIsPreviewing(false)} />
    </div>
  );
};

export default ElinxForm;
